import type { SmartCreationDraft, SmartCreationObjectiveRecommendation } from "@adpilot/shared";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { CampaignCreationInvalidProduct } from "@/lib/campaign-creation-session";
import {
  clearSmartCreationLocalState,
  writeSmartCreationLocalState,
} from "@/lib/smart-creation-local-state";
import { draftsMatch } from "./draft-rules";
import { usePlanIdentityReset } from "./use-plan-identity-reset";
import type { PlanSession } from "./use-plan-session";

/**
 * Browser-only inputs of the Products and Objectives steps. Draft edits stay local (and in
 * local storage) until the step is saved with its next durable command.
 */
export function useStepInputs(session: PlanSession) {
  const { t } = useTranslation("smartCreation");
  const {
    actionLock,
    browserDraftRef,
    draftRef,
    editVersionRef,
    localChangesRef,
    revisionConflict,
    setDraft,
  } = session;
  const [objectiveRecommendation, setObjectiveRecommendation] =
    useState<SmartCreationObjectiveRecommendation | null>(null);
  const [invalidProducts, setInvalidProducts] = useState<CampaignCreationInvalidProduct[]>([]);
  const [catalogBlocked, setCatalogBlocked] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [promotionValidationRequested, setPromotionValidationRequested] = useState(false);
  const [enterInput, setEnterInput] = useState("");

  usePlanIdentityReset(session.identity, () => {
    setObjectiveRecommendation(null);
    setInvalidProducts([]);
    setCatalogBlocked(false);
    setValidationError(null);
    setPromotionValidationRequested(false);
    setEnterInput("");
  });

  const changeLocalDraft = useCallback(
    (nextDraft: SmartCreationDraft, preserveRecommendation = false) => {
      if (actionLock.current || revisionConflict) return;
      const currentPlan = browserDraftRef.current;
      const matchesComputedDraft = currentPlan ? draftsMatch(nextDraft, currentPlan.draft) : false;
      editVersionRef.current += 1;
      localChangesRef.current = !matchesComputedDraft;
      draftRef.current = nextDraft;
      setDraft(nextDraft);
      if (currentPlan) {
        if (matchesComputedDraft) {
          clearSmartCreationLocalState(currentPlan.shopId, currentPlan.id);
        } else {
          if (
            !writeSmartCreationLocalState(
              currentPlan.shopId,
              currentPlan.id,
              currentPlan.revision,
              nextDraft,
            )
          ) {
            toast.warning(t("errors.localStorage"), { id: "smart-creation-storage" });
          }
        }
      }
      if (!preserveRecommendation) setObjectiveRecommendation(null);
    },
    [
      actionLock,
      browserDraftRef,
      draftRef,
      editVersionRef,
      localChangesRef,
      revisionConflict,
      setDraft,
      t,
    ],
  );

  return {
    objectiveRecommendation,
    setObjectiveRecommendation,
    invalidProducts,
    setInvalidProducts,
    catalogBlocked,
    setCatalogBlocked,
    validationError,
    setValidationError,
    promotionValidationRequested,
    setPromotionValidationRequested,
    enterInput,
    setEnterInput,
    changeLocalDraft,
  };
}

export type StepInputs = ReturnType<typeof useStepInputs>;
